import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Formik,
  Form,
  Field,
} from 'formik'
import * as Yup from 'yup'
import { List, Map } from 'immutable'
import Col from 'muicss/lib/react/col'
import Row from 'muicss/lib/react/row'
import cloneDeep from 'lodash/cloneDeep'
import {
  updateCampaign as updateCampaignAction,
  getAllAmbassadorsData,
} from 'services/ambassador/redux/actions'
import Button from 'muicss/lib/react/button'
import Container from 'muicss/lib/react/container'
import { getViewRecordsAsList } from 'services/data'
import { selectData } from 'services/data/redux/selectors'
import { dismissModal as dismissModalAction } from 'services/modal/redux/actions'
import { AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY, AMBASSADOR_PARTNER_OPTIONS_KEY } from 'services/ambassador'
import { MultiSelect } from '../../MultiSelect'

import './index.scss'

const VALIDATION_SCHEMA = Yup.object()
  .shape({
    uuid: Yup.string(),
    addAmbassadors: Yup.array().min(1, 'Select at least one partner'),
  })

const getInitialValues = () => {
  const data = useSelector(selectData)
  const uuid = data.getIn([AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY, 'records', 'uuid'], '')

  return {
    uuid,
    addAmbassadors: [],
  }
}

/**
 * Get the ambassador select options
 *
 * @returns {Array}
 */
const ambassadorsOptions = (recordOptions) => {
  const data = useSelector(selectData)
  const options = Map(recordOptions)
  const viewInstance = { data, storeKey: AMBASSADOR_PARTNER_OPTIONS_KEY, options }
  const ambassadorsData = getViewRecordsAsList(viewInstance) || List()

  const campaignAmbassadors = data
    .getIn([AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY, 'records', 'ambassadors'], Map())
    .map((ambassador) => ambassador.get('uuid'))
  const availableAmbassadors = []

  ambassadorsData.forEach((partner) => {
    const uuid = partner.get('uuid')

    if (campaignAmbassadors.includes(uuid)) {
      return
    }

    availableAmbassadors.push({ value: uuid, label: partner.get('name') })
  })

  return availableAmbassadors
}

/**
 * Form to add partners to an existing campaign
 *
 * @returns {JSX.Element}
 */
const AddPartnerModal = () => {
  const dispatch = useDispatch()
  const recordOptions = {
    perPage: 1000,
    sortField: 'name',
    sortDirection: 'ASC',
    activeOnly: true,
  }

  const onDismissHandler = (e) => {
    e.persist()
    dispatch(dismissModalAction())
  }

  const handleSubmit = (values) => {
    const newData = cloneDeep(values)
    newData.addAmbassadors = newData.addAmbassadors.map((ambassador) => ambassador.value)

    dispatch(updateCampaignAction(newData))
  }

  useEffect(() => {
    dispatch(getAllAmbassadorsData({
      storeKey: AMBASSADOR_PARTNER_OPTIONS_KEY,
      options: recordOptions,
    }))
  }, [])

  return (
    <div className="add-partner-modal">
      <h2 className="add-partner-modal__title">Add Partner to Campaign</h2>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
        className="add-partner-modal__form"
      >
        {({
          errors,
          touched,
        }) => (
          <Form>
            <Container fluid={true}>
              <div className="add-partner-modal__container">
                <Row>
                  <Col sm="12" md="12">
                    <span className="custom-label">Name</span>
                    <div className="add-partner-modal__multi-select">
                      <Field
                        component={MultiSelect}
                        name="addAmbassadors"
                        options={ambassadorsOptions(recordOptions)}
                        placeholder="Select partner name(s)"
                        invalid={touched.addAmbassadors && errors.addAmbassadors}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12" md="6">
                    <Button
                      variant="raised"
                      type="submit"
                    >
                      ADD
                    </Button>
                    <Button
                      type="button"
                      variant="raised"
                      color="accent"
                      className="mui-btn--secondary"
                      onClick={onDismissHandler}
                    >
                      CANCEL
                    </Button>
                  </Col>
                </Row>
              </div>
            </Container>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default (AddPartnerModal)
